import { isVoided } from '../../utils/records';
import { isGuest } from './guest';
import type { Family, Member } from '../../types';

// Field-level validation for the member form.
//
// Kept out of the screen so it can be tested without rendering anything, and so
// the duplicate rules — which are the fiddly part — are readable in one place.
//
// Everything returns a message keyed by field name; an empty object means the
// form is good. The screen renders each message under its own input rather than
// as a toast, so the user can see which field is wrong.

/** Case- and space-insensitive, so "John  Samuel" matches "john samuel". */
const norm = (v?: string) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Marital statuses that imply a wedding, so a date of marriage applies. Widowed
 * and Divorced still had one; Single never did.
 *
 * The form hides the Date of Marriage field for anyone outside this list and
 * drops the value on save, so `dateOfMarriage` and `maritalStatus` cannot
 * contradict each other in the register.
 */
export const MARRIED_STATUSES = ['Married', 'Widowed', 'Divorced'];

export const hasMarriageDate = (maritalStatus?: string) =>
  MARRIED_STATUSES.includes(String(maritalStatus || '').trim());

/** The bare family ID, from either "FAM-12" or "FAM-12 - John Samuel". */
export const familyIdOf = (value?: string) => String(value || '').split(' - ')[0].trim();

/** The form allows a blank gender, which the saved record does not. */
export type MemberFormLike = Omit<Partial<Member>, 'gender'> & {
  gender?: Member['gender'] | '';
};

export interface ValidateMemberOptions {
  /** Head mode: the family is being created here, so there is no link to pick. */
  skipFamilyLink?: boolean;
  /** Head mode: the head's mobile becomes the family contact, so it is required. */
  requireMobile?: boolean;
}

/**
 * @param form       the record being saved
 * @param allMembers every member, including deleted ones
 * @param families   for checking the family link resolves
 * @param editingId  the row being edited, excluded from duplicate checks
 * @param opts       mode adjustments (Head vs Member add)
 */
const validateMember = (
  form: MemberFormLike,
  allMembers: Member[] = [],
  families: Family[] = [],
  editingId?: number | null,
  opts: ValidateMemberOptions = {}
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // A deleted record must never block re-entering the same person, and the row
  // being edited must not collide with itself.
  const others = allMembers.filter((m) => m.id !== editingId && !isVoided(m));

  // --- Required ---
  const name = String(form.name || '').trim();
  if (!name) errors.name = "The member's name is required.";
  else if (name.length < 3) errors.name = 'Enter the full name (at least 3 characters).';

  // In Head mode the family is created alongside, so there is nothing to link
  // and no relation to pick (the person is the Head).
  if (!opts.skipFamilyLink) {
    const familyId = familyIdOf(form.familyId);
    if (!familyId) {
      errors.familyId = 'Link this member to a family.';
    } else if (families.length && !families.some((f) => (f.familyId || `FAM-${f.id}`) === familyId)) {
      // Typing a value that matches no family used to save silently, leaving the
      // member pointing at a family that does not exist.
      errors.familyId = `No family with ID "${familyId}". Pick one from the list.`;
    }

    if (!String(form.relationToHead || '').trim()) errors.relationToHead = 'Select the relation to the head of family.';
  }
  if (!String(form.gender || '').trim()) errors.gender = 'Select a gender.';
  if (!String(form.maritalStatus || '').trim()) errors.maritalStatus = 'Select a marital status.';

  const dob = String(form.dateOfBirth || '').trim();
  if (!dob) {
    errors.dateOfBirth = 'Date of birth is required.';
  } else if (dob > new Date().toISOString().slice(0, 10)) {
    errors.dateOfBirth = 'Date of birth cannot be in the future.';
  }

  // --- Format ---
  const mobile = String(form.mobileNumber || '').trim();
  if (opts.requireMobile && !mobile) {
    errors.mobileNumber = 'Mobile number is required — it is the family contact.';
  } else if (mobile && !/^\d{7,12}$/.test(mobile)) {
    // 7 to 12 digits: Indian mobiles, landlines and numbers abroad.
    errors.mobileNumber = 'Enter a mobile number of 7 to 12 digits.';
  }

  const aadhaar = String(form.aadhaarNumber || '').trim();
  if (aadhaar && !/^\d{12}$/.test(aadhaar)) {
    errors.aadhaarNumber = 'Aadhaar number must be exactly 12 digits.';
  }

  // --- Conditional on life status ---
  if (form.status === 'Deceased') {
    const dod = String(form.dateOfDeath || '').trim();
    if (!dod) errors.dateOfDeath = 'Record the date of death.';
    else if (dob && dod < dob) errors.dateOfDeath = 'The date of death cannot be before the date of birth.';
  }

  if (form.status === 'Transferred' && !String(form.transferChurch || '').trim()) {
    errors.transferChurch = 'Record which church the member moved to.';
  }

  // --- Guest stay ---
  // A guest needs a start; the end is optional (open-ended) but must follow it.
  if (isGuest(form)) {
    const from = String(form.guestFrom || '').trim();
    const until = String(form.guestUntil || '').trim();
    if (!from) errors.guestFrom = 'Record when the guest stay began.';
    else if (dob && from < dob) errors.guestFrom = 'The guest stay cannot begin before the date of birth.';
    if (until && from && until <= from) errors.guestUntil = 'The stay must end after it begins.';
  }

  // --- Dates that must follow the date of birth ---
  if (dob) {
    const notBeforeBirth: Array<[string, string, string]> = [
      // Only while the field is on show. An older record edited down to Single
      // still carries its marriage date, and the save drops it — complaining
      // about a hidden field would block the save with no way to fix it.
      ...(hasMarriageDate(form.maritalStatus)
        ? [['dateOfMarriage', String(form.dateOfMarriage || ''), 'The date of marriage'] as [string, string, string]]
        : []),
      ['baptismDate', String(form.baptismDate || ''), 'The baptism date'],
      ['confirmationDate', String(form.confirmationDate || ''), 'The confirmation date']
    ];
    notBeforeBirth.forEach(([field, value, label]) => {
      if (value && value < dob) errors[field] = `${label} cannot be before the date of birth.`;
    });
  }

  const baptism = String(form.baptismDate || '');
  const confirmation = String(form.confirmationDate || '');
  if (baptism && confirmation && confirmation < baptism && !errors.confirmationDate) {
    errors.confirmationDate = 'Confirmation cannot be dated before baptism.';
  }

  // --- Duplicates across the register ---
  // A shared mobile is not an error (a household shares one phone), and nor is
  // the same name and date of birth (dates are sometimes approximate): the
  // server reports both as warnings the user can save past (duplicate-check).

  // A shared Aadhaar is always a double entry — it identifies one person.
  if (aadhaar && others.some((m) => String(m.aadhaarNumber || '').trim() === aadhaar)) {
    errors.aadhaarNumber = 'This Aadhaar number is already on another member.';
  }

  const membershipNo = norm(form.barcodeMembershipNo);
  if (membershipNo && others.some((m) => norm(m.barcodeMembershipNo) === membershipNo)) {
    errors.barcodeMembershipNo = 'This membership number is already in use.';
  }

  return errors;
};

export default validateMember;
